import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Check, Loader2, Pencil, Pin, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";
const smallInputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

function EditRow({ video, onSaved, onCancel }) {
  const [title, setTitle] = useState(video.title || "");
  const [description, setDescription] = useState(video.description || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return toast.error("Title can't be empty");
    setBusy(true);
    try {
      await api.patch(`/admin/videos/${video.id}`, { title: title.trim(), description: description.trim() });
      toast.success("Saved");
      onSaved({ ...video, title: title.trim(), description: description.trim() });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0 flex-1 space-y-1.5" data-testid={`admin-video-edit-box-${video.id}`}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className={smallInputCls}
        data-testid={`admin-video-edit-title-${video.id}`}
        autoFocus
      />
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Short description (optional)"
        className={smallInputCls}
        data-testid={`admin-video-edit-desc-${video.id}`}
      />
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid={`admin-video-edit-save-${video.id}`}
          className="flex items-center gap-1 rounded-full bg-emerald-300 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid={`admin-video-edit-cancel-${video.id}`}
          className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-300 hover:bg-white/5"
        >
          <X size={11} />
          Cancel
        </button>
      </div>
    </div>
  );
}

export function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/admin/videos").then((r) => setVideos(r.data));
  useEffect(() => {
    load().catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a video file first.");
    setSaving(true);
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("video", file);
    try {
      await api.post("/admin/videos", fd, { timeout: 300000 });
      setTitle("");
      setDescription("");
      setFile(null);
      load();
      toast.success("Video uploaded to the showcase");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const commitOrder = async (next) => {
    setVideos(next);
    setSavingOrder(true);
    try {
      await api.patch("/admin/videos/order", { ids: next.map((v) => v.id) });
    } catch {
      toast.error("Couldn't save order — refreshing");
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= videos.length) return;
    const next = [...videos];
    [next[idx], next[target]] = [next[target], next[idx]];
    commitOrder(next);
  };

  const pin = (idx) => {
    if (idx === 0) return;
    const next = [videos[idx], ...videos.filter((_, i) => i !== idx)];
    commitOrder(next);
  };

  const remove = async (vid) => {
    if (!window.confirm("Delete this video from the showcase?")) return;
    await api.delete(`/admin/videos/${vid}`);
    load();
  };

  const applyEdit = (updated) => {
    setVideos((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
    setEditingId(null);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/10 bg-[#141419] p-5 lg:col-span-2" data-testid="admin-video-form">
        <h3 className="font-heading font-bold text-white">Upload Video</h3>
        <input required placeholder="Title (e.g. Studio Session Recap)" value={title}
          onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="admin-video-title-input" />
        <textarea rows={3} placeholder="Short description (optional)" value={description}
          onChange={(e) => setDescription(e.target.value)} className={inputCls} data-testid="admin-video-desc-input" />
        <label className="block rounded-lg border border-dashed border-white/15 p-3 text-xs text-zinc-400" data-testid="admin-video-file-input">
          Video file (MP4/MOV/WEBM, max 400MB) *
          <input type="file" accept="video/*" className="mt-2 block w-full text-xs" onChange={(e) => setFile(e.target.files[0])} />
        </label>
        <button type="submit" disabled={saving} data-testid="admin-video-upload-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {saving ? "Uploading…" : "Upload Video"}
        </button>
        <p className="text-[11px] text-zinc-600">Videos appear on the Showcase page. Short loops (under 30s) work best.</p>
      </form>

      <div className="space-y-3 lg:col-span-3" data-testid="admin-videos-list">
        {videos.length > 0 && (
          <p className="flex items-center gap-2 text-[11px] text-zinc-500">
            Reorder, rename or remove — the top row is pinned as the featured recap on /showcase.
            {savingOrder && <Loader2 size={11} className="animate-spin text-emerald-300" />}
          </p>
        )}
        {videos.map((v, idx) => (
          <div
            key={v.id}
            className={`flex items-start gap-3 rounded-2xl border p-3 ${
              idx === 0 ? "border-emerald-300/50 bg-emerald-300/5" : "border-white/10 bg-[#141419]"
            }`}
            data-testid={`admin-video-row-${v.id}`}
          >
            <video src={fileUrl(v.video_url)} muted loop playsInline autoPlay className="h-14 w-24 shrink-0 rounded-lg object-cover" />
            {editingId === v.id ? (
              <EditRow video={v} onSaved={applyEdit} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-bold text-white" data-testid={`admin-video-title-${v.id}`}>{v.title}</p>
                  {idx === 0 && (
                    <span className="rounded-full bg-emerald-300 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-black">
                      Featured
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-zinc-500">{v.description || "No description"}</p>
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setEditingId(editingId === v.id ? null : v.id)}
                data-testid={`admin-video-edit-${v.id}`}
                aria-label="Edit"
                className="rounded-full border border-white/10 p-2 text-emerald-200 hover:border-emerald-300/40 hover:bg-emerald-300/10"
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                onClick={() => pin(idx)}
                disabled={idx === 0 || savingOrder}
                data-testid={`admin-video-pin-${v.id}`}
                aria-label="Pin to top"
                className="rounded-full border border-white/10 p-2 text-emerald-200 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <Pin size={13} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0 || savingOrder}
                data-testid={`admin-video-up-${v.id}`}
                aria-label="Move up"
                className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === videos.length - 1 || savingOrder}
                data-testid={`admin-video-down-${v.id}`}
                aria-label="Move down"
                className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowDown size={13} />
              </button>
              <button
                onClick={() => remove(v.id)}
                data-testid={`admin-video-delete-${v.id}`}
                aria-label="Delete"
                className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {videos.length === 0 && <p className="text-sm text-zinc-600">No videos uploaded yet.</p>}
      </div>
    </div>
  );
}
